import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { LogIn, RefreshCcw } from "lucide-react";

export default function VerifyNotice() {
  const location = useLocation();
  const email = location.state?.email;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // If no email was passed, redirect to register page
    if (!email) {
      navigate("/register", { replace: true });
    }
  }, [email, navigate]);

  // Countdown logic
  useEffect(() => {
    let interval;
    if (email && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [email, timer]);

  // Prevent flicker while redirecting
  if (!email) { 
    return null;
  }

  // Handle resend verification email
  const handleResend = async () => {
    if (!email) {
      setMessage("Email not found. Please register again.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(`${BASE_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // In case cookies are needed later
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.message || "Failed to resend verification email.");
      } else {
        setMessage("Verification email resent successfully! Check your inbox.");
        setTimer(60); // Start 60s countdown
      }
    } catch (error) {
      console.error("Resend error:", error);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[80vh] bg-gray-50 px-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        <h1 className="text-2xl font-semibold text-green-600 mb-3">
          Registration Successful 🎉
        </h1>
        <p className="text-gray-700 mb-2">We've sent a verification link to:</p>
        <p className="font-medium text-gray-900">{email || "your email"}</p>
        <p className="mt-4 text-gray-600">
          Please check your inbox and verify your account before logging in.
        </p>

        {message && (
          <p className={`mt-4 text-sm ${message.includes("successfully") ? "text-green-600" : "text-red-600"}`}>
            {message}
          </p>
        )}

        <div className="mt-6 space-y-3">
          <Link
            to="/signin"
            className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
          >
            <LogIn className="w-4 h-4" /> Go to Sign In
          </Link>

          <button
            onClick={handleResend}
            disabled={loading || timer > 0}
            className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition ${
              loading || timer > 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            <RefreshCcw className="w-4 h-4" />
            {loading
              ? "Resending..."
              : timer > 0
              ? `Resend in ${timer}s`
              : "Resend Verification Email"}
          </button>
        </div>
      </div>
    </div>
  );
}
